import { supabase } from '../supabase';
import { persistClientReferenti } from './persistClientContacts';

/** Cifre significative per il confronto/dedup di un numero (ultime 9, ignora prefisso/zeri). */
function phoneKey(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  return digits.slice(-9);
}

/**
 * Salva i numeri inseriti per il gruppo WhatsApp anche come contatti del cliente
 * (`client_contacts`), saltando quelli con un telefono già presente (dedup).
 *
 * Best-effort: gli errori vengono loggati ma NON propagati (non deve bloccare il
 * flusso di firma). I nuovi contatti vengono creati con `area: 'altro'`.
 *
 * Ritorna il numero di contatti creati.
 */
export async function saveGroupNumbersAsContacts(
  clientId: string,
  numbers: string[],
): Promise<number> {
  try {
    const cleaned = numbers.map((n) => (n || '').trim()).filter((n) => n !== '');
    if (cleaned.length === 0) return 0;

    // organization_id del cliente (richiesto da client_contacts)
    const { data: client } = await supabase
      .from('clients')
      .select('organization_id')
      .eq('id', clientId)
      .maybeSingle();
    const organizationId = client?.organization_id as string | undefined;
    if (!organizationId) return 0;

    // Telefoni già presenti tra i contatti del cliente → dedup
    const { data: existing } = await supabase
      .from('client_contacts')
      .select('phone')
      .eq('client_id', clientId);
    const existingKeys = new Set(
      (existing ?? [])
        .map((c: { phone: string | null }) => (c.phone ? phoneKey(c.phone) : ''))
        .filter(Boolean),
    );

    // Dedup anche all'interno dei numeri in input
    const seen = new Set<string>();
    const toInsert = cleaned
      .filter((n) => {
        const key = phoneKey(n);
        if (!key || existingKeys.has(key) || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((phone) => ({
        client_id: clientId,
        organization_id: organizationId,
        name: 'Referente WhatsApp',
        phone,
        area: 'altro',
        is_active: true,
      }));

    if (toInsert.length === 0) return 0;

    // Modello Contatti first-class: ogni numero diventa una persona
    // clients(individual) + link client_relationships('contatto').
    await persistClientReferenti(
      clientId,
      organizationId,
      toInsert.map((c) => ({ name: c.name, phone: c.phone, area: 'altro', is_primary: false })) as any,
    );
    return toInsert.length;
  } catch (e) {
    console.error('[saveGroupNumbersAsContacts] failed:', e);
    return 0;
  }
}
